import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { generatedFileResponseSchema } from "./imports.js";
import { stockQtySchema, stockVariantSchema } from "./inventory.js";
import { paisa } from "./money.js";
/** Purchase orders, goods receipts and purchase returns (docs/specs/purchasing.md, P-02, P-03, P-05). */
export const PURCHASE_ORDER_STATUSES = [
    "draft",
    "sent",
    "partially_received",
    "received",
    "closed",
    "cancelled",
];
export const purchaseOrderStatusSchema = z.enum(PURCHASE_ORDER_STATUSES);
export const MAX_PURCHASE_LINES = 500;
const positiveQty = stockQtySchema.refine((value) => BigInt(value) > 0n, "Must be more than zero");
const note = z.string().trim().max(500);
const version = z.int().min(1);
const step = z.object({ at: z.iso.datetime().nullable(), by: z.uuid().nullable() });
const cursorQuery = {
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
};
const distinctVariants = (lines) => new Set(lines.map((line) => line.variantId.toLowerCase())).size === lines.length;
const lineVariant = stockVariantSchema;
// Purchase orders --------------------------------------------------------------------------------------------------
export const purchaseOrderSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    supplierId: z.uuid(),
    locationId: z.uuid(),
    status: purchaseOrderStatusSchema,
    expectedDate: z.iso.date().nullable(),
    note: z.string(),
    lineCount: z.int(),
    totalPaisa: paisa(),
    sent: step,
    closed: step,
    cancelled: step,
    version: z.int(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const purchaseOrderLineSchema = z.object({
    id: z.uuid(),
    variantId: z.uuid(),
    variant: lineVariant.extend({ barcodes: z.array(z.string()) }),
    orderedQty: stockQtySchema,
    receivedQty: stockQtySchema,
    /** Per whole unit (per kg for a kg product). */
    unitCostPaisa: paisa(),
    lineTotalPaisa: paisa(),
});
export const purchaseOrderDetailSchema = purchaseOrderSchema.extend({
    lines: z.array(purchaseOrderLineSchema),
});
export const purchaseOrderListQuerySchema = z.object({
    status: purchaseOrderStatusSchema.optional(),
    supplierId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    ...cursorQuery,
});
export const purchaseOrderListSchema = z.object({
    data: z.array(purchaseOrderSchema),
    nextCursor: z.uuid().nullable(),
});
const orderLines = z
    .array(z.object({ variantId: z.uuid(), qty: positiveQty, unitCostPaisa: paisa().min(0) }).strict())
    .min(1)
    .max(MAX_PURCHASE_LINES)
    .refine(distinctVariants, "Each variant once");
/** `POST /v1/purchase-orders`: a draft. */
export const createPurchaseOrderRequestSchema = z
    .object({
    supplierId: z.uuid(),
    locationId: z.uuid(),
    expectedDate: z.iso.date().nullable().default(null),
    note: note.default(""),
    lines: orderLines,
})
    .strict();
/** `PATCH /v1/purchase-orders/{id}`: a draft's fields; `lines` replaces them all. */
export const updatePurchaseOrderRequestSchema = z
    .object({
    supplierId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    expectedDate: z.iso.date().nullable().optional(),
    note: note.optional(),
    lines: orderLines.optional(),
    version,
})
    .strict();
export const purchaseOrderActionRequestSchema = z.object({ version }).strict();
// Goods receipts ---------------------------------------------------------------------------------------------------
export const goodsReceiptSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    supplierId: z.uuid(),
    locationId: z.uuid(),
    purchaseOrderId: z.uuid().nullable(),
    supplierInvoiceRef: z.string(),
    receivedAt: z.iso.datetime(),
    note: z.string(),
    lineCount: z.int(),
    totalPaisa: paisa(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const goodsReceiptLineSchema = z.object({
    id: z.uuid(),
    variantId: z.uuid(),
    variant: lineVariant,
    purchaseOrderLineId: z.uuid().nullable(),
    qty: stockQtySchema,
    unitCostPaisa: paisa(),
    lineTotalPaisa: paisa(),
    /** Already sent back to the supplier from this line. */
    returnedQty: stockQtySchema,
});
export const goodsReceiptDetailSchema = goodsReceiptSchema.extend({ lines: z.array(goodsReceiptLineSchema) });
export const goodsReceiptListQuerySchema = z.object({
    supplierId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    purchaseOrderId: z.uuid().optional(),
    ...cursorQuery,
});
export const goodsReceiptListSchema = z.object({
    data: z.array(goodsReceiptSchema),
    nextCursor: z.uuid().nullable(),
});
/**
 * `POST /v1/goods-receipts`: against a purchase order (supplier and location come from it; lines name its lines)
 * or direct (P-03: supplier, location and any stock-tracked variants). Posted at once: `purchase_receipt` entries
 * at the given cost, which goes into the moving average (I-09).
 */
export const createGoodsReceiptRequestSchema = z
    .object({
    purchaseOrderId: z.uuid().optional(),
    supplierId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    supplierInvoiceRef: z.string().trim().max(100).default(""),
    receivedAt: z.iso.datetime({ offset: true }).optional(),
    note: note.default(""),
    lines: z
        .array(z
        .object({
        variantId: z.uuid(),
        qty: positiveQty,
        unitCostPaisa: paisa().min(0),
        purchaseOrderLineId: z.uuid().optional(),
    })
        .strict())
        .min(1)
        .max(MAX_PURCHASE_LINES)
        .refine(distinctVariants, "Each variant once"),
})
    .strict()
    .refine((r) => r.purchaseOrderId !== undefined || (r.supplierId !== undefined && r.locationId !== undefined), {
    message: "A direct receipt needs a supplier and a location",
    path: ["supplierId"],
});
// Purchase returns ---------------------------------------------------------------------------------------------------
export const PURCHASE_RETURN_REASONS = [
    "damaged",
    "wrong_item",
    "expired",
    "excess",
    "quality",
    "other",
];
export const purchaseReturnReasonSchema = z.enum(PURCHASE_RETURN_REASONS);
export const purchaseReturnSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    supplierId: z.uuid(),
    locationId: z.uuid(),
    goodsReceiptId: z.uuid().nullable(),
    reason: purchaseReturnReasonSchema,
    returnedAt: z.iso.datetime(),
    note: z.string(),
    lineCount: z.int(),
    /** What the supplier owes back: it lowers the payable. */
    totalPaisa: paisa(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const purchaseReturnLineSchema = z.object({
    id: z.uuid(),
    variantId: z.uuid(),
    variant: lineVariant,
    goodsReceiptLineId: z.uuid().nullable(),
    qty: stockQtySchema,
    unitCostPaisa: paisa(),
    lineTotalPaisa: paisa(),
});
export const purchaseReturnDetailSchema = purchaseReturnSchema.extend({
    lines: z.array(purchaseReturnLineSchema),
});
export const purchaseReturnListQuerySchema = z.object({
    supplierId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    ...cursorQuery,
});
export const purchaseReturnListSchema = z.object({
    data: z.array(purchaseReturnSchema),
    nextCursor: z.uuid().nullable(),
});
/**
 * `POST /v1/purchase-returns`: goods back to the supplier (P-05). With a receipt, lines may not exceed what it
 * brought in less what was already returned, and the cost defaults to the receipt's; without one, a supplier and a
 * location, and the cost defaults to the current average.
 */
export const createPurchaseReturnRequestSchema = z
    .object({
    goodsReceiptId: z.uuid().optional(),
    supplierId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    reason: purchaseReturnReasonSchema,
    note: note.default(""),
    lines: z
        .array(z
        .object({ variantId: z.uuid(), qty: positiveQty, unitCostPaisa: paisa().min(0).optional() })
        .strict())
        .min(1)
        .max(MAX_PURCHASE_LINES)
        .refine(distinctVariants, "Each variant once"),
})
    .strict()
    .refine((r) => r.goodsReceiptId !== undefined || (r.supplierId !== undefined && r.locationId !== undefined), {
    message: "A return without a receipt needs a supplier and a location",
    path: ["supplierId"],
})
    .refine((r) => r.reason !== "other" || r.note.trim() !== "", { message: "Say why", path: ["note"] });
// Events -------------------------------------------------------------------------------------------------------------
const eventLine = z.object({
    variantId: z.uuid(),
    qty: stockQtySchema,
    unitCostPaisa: paisa(),
    lineTotalPaisa: paisa(),
});
export const purchaseOrderEventSchema = purchaseOrderSchema;
export const goodsReceiptEventSchema = goodsReceiptSchema.extend({ lines: z.array(eventLine) });
export const purchaseReturnEventSchema = purchaseReturnSchema.extend({ lines: z.array(eventLine) });
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "purchasing" };
const view = { permission: "purchasing.view" };
const order = { permission: "purchase_orders.manage" };
const receive = { permission: "goods.receive" };
export const listPurchaseOrders = defineEndpoint({
    ...tag,
    name: "listPurchaseOrders",
    method: "GET",
    path: "/v1/purchase-orders",
    access: view,
    summary: "Purchase orders for the caller's locations, newest first",
    query: purchaseOrderListQuerySchema,
    status: 200,
    response: purchaseOrderListSchema,
});
export const createPurchaseOrder = defineEndpoint({
    ...tag,
    name: "createPurchaseOrder",
    method: "POST",
    path: "/v1/purchase-orders",
    access: order,
    summary: "Start a purchase order (draft)",
    body: createPurchaseOrderRequestSchema,
    status: 201,
    response: purchaseOrderDetailSchema,
    errors: ["STOCK_NOT_TRACKED", "LOCATION_INACTIVE", "SUPPLIER_ARCHIVED"],
});
export const getPurchaseOrder = defineEndpoint({
    ...tag,
    name: "getPurchaseOrder",
    method: "GET",
    path: "/v1/purchase-orders/{id}",
    access: view,
    summary: "One purchase order with its lines and what has arrived",
    params: idParamsSchema,
    status: 200,
    response: purchaseOrderDetailSchema,
});
export const updatePurchaseOrder = defineEndpoint({
    ...tag,
    name: "updatePurchaseOrder",
    method: "PATCH",
    path: "/v1/purchase-orders/{id}",
    access: order,
    summary: "Change a draft",
    params: idParamsSchema,
    body: updatePurchaseOrderRequestSchema,
    status: 200,
    response: purchaseOrderDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION", "STOCK_NOT_TRACKED", "LOCATION_INACTIVE", "SUPPLIER_ARCHIVED"],
});
const orderAction = (name, verb, summary) => defineEndpoint({
    ...tag,
    name,
    method: "POST",
    path: `/v1/purchase-orders/{id}/${verb}`,
    access: order,
    summary,
    params: idParamsSchema,
    body: purchaseOrderActionRequestSchema,
    status: 200,
    response: purchaseOrderDetailSchema,
    errors: ["CONFLICT", "INVALID_TRANSITION"],
});
export const sendPurchaseOrder = orderAction("sendPurchaseOrder", "send", "Mark as sent to the supplier");
export const closePurchaseOrder = orderAction("closePurchaseOrder", "close", "Close: nothing more is expected (short close)");
export const cancelPurchaseOrder = orderAction("cancelPurchaseOrder", "cancel", "Cancel before anything has arrived");
export const purchaseOrderPdf = defineEndpoint({
    ...tag,
    name: "purchaseOrderPdf",
    method: "POST",
    path: "/v1/purchase-orders/{id}/pdf",
    access: view,
    summary: "The purchase order as a PDF (signed URL)",
    params: idParamsSchema,
    status: 201,
    response: generatedFileResponseSchema,
});
export const listGoodsReceipts = defineEndpoint({
    ...tag,
    name: "listGoodsReceipts",
    method: "GET",
    path: "/v1/goods-receipts",
    access: view,
    summary: "Goods receipts at the caller's locations, newest first",
    query: goodsReceiptListQuerySchema,
    status: 200,
    response: goodsReceiptListSchema,
});
export const createGoodsReceipt = defineEndpoint({
    ...tag,
    name: "createGoodsReceipt",
    method: "POST",
    path: "/v1/goods-receipts",
    access: receive,
    summary: "Book goods received (against a purchase order or direct)",
    body: createGoodsReceiptRequestSchema,
    status: 201,
    response: goodsReceiptDetailSchema,
    errors: [
        "INVALID_TRANSITION",
        "OVER_RECEIPT",
        "STOCK_NOT_TRACKED",
        "LOCATION_INACTIVE",
        "SUPPLIER_ARCHIVED",
    ],
    idempotent: true,
});
export const getGoodsReceipt = defineEndpoint({
    ...tag,
    name: "getGoodsReceipt",
    method: "GET",
    path: "/v1/goods-receipts/{id}",
    access: view,
    summary: "One goods receipt with its lines",
    params: idParamsSchema,
    status: 200,
    response: goodsReceiptDetailSchema,
});
export const listPurchaseReturns = defineEndpoint({
    ...tag,
    name: "listPurchaseReturns",
    method: "GET",
    path: "/v1/purchase-returns",
    access: view,
    summary: "Returns to suppliers at the caller's locations, newest first",
    query: purchaseReturnListQuerySchema,
    status: 200,
    response: purchaseReturnListSchema,
});
export const createPurchaseReturn = defineEndpoint({
    ...tag,
    name: "createPurchaseReturn",
    method: "POST",
    path: "/v1/purchase-returns",
    access: receive,
    summary: "Send goods back to a supplier: purchase_return entries, payable reduced",
    body: createPurchaseReturnRequestSchema,
    status: 201,
    response: purchaseReturnDetailSchema,
    errors: ["OVER_RETURN", "INSUFFICIENT_STOCK", "STOCK_NOT_TRACKED", "LOCATION_INACTIVE"],
    idempotent: true,
});
export const getPurchaseReturn = defineEndpoint({
    ...tag,
    name: "getPurchaseReturn",
    method: "GET",
    path: "/v1/purchase-returns/{id}",
    access: view,
    summary: "One purchase return with its lines",
    params: idParamsSchema,
    status: 200,
    response: purchaseReturnDetailSchema,
});
//# sourceMappingURL=purchases.js.map